import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fmt, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { CustomerPicker } from "@/components/CustomerPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, FileText, Copy, Trash2, ChevronRight, Building2 } from "lucide-react";

const STATUS = {
  draft: { label: "Koncept", cls: "" },
  sent: { label: "Odesláno", cls: "border-secondary/40 text-secondary bg-secondary/5" },
  accepted: { label: "Přijato", cls: "border-primary/40 text-primary bg-primary/5" },
};

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", valid_until: "" });
  const [customer, setCustomer] = useState(null);
  const [custOpen, setCustOpen] = useState(false);
  const nav = useNavigate();

  const load = useCallback(() => api.get("/offers").then((r) => setOffers(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Zadejte název nabídky"); return; }
    const r = await api.post("/offers", { ...form, customer_id: customer?.id || null });
    toast.success("Nabídka vytvořena");
    setOpen(false); setForm({ name: "", valid_until: "" }); setCustomer(null);
    nav(`/nabidky/${r.data.id}`);
  };
  const duplicate = async (id) => { const r = await api.post(`/offers/${id}/duplicate`); toast.success("Zduplikováno"); nav(`/nabidky/${r.data.id}`); };
  const del = async (id) => { await api.delete(`/offers/${id}`); toast.success("Smazáno"); load(); };

  return (
    <div className="fade-up">
      <PageHeader title="Nabídky" subtitle="Cenové nabídky pro odběratele s množstevními slevami">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="rounded-full" data-testid="offer-new-btn"><Plus className="h-4 w-4 mr-2" /> Nová nabídka</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display">Nová nabídka</DialogTitle></DialogHeader>
            <div className="space-y-3 py-1">
              <div><Label>Název *</Label><Input data-testid="offer-form-name" className="mt-1.5" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="např. Nabídka jídel Q3" /></div>
              <div>
                <Label>Odběratel</Label>
                <button onClick={() => setCustOpen(true)} data-testid="offer-form-customer"
                  className="mt-1.5 w-full flex items-center gap-2 border border-border rounded-lg px-3 py-2.5 text-left hover:bg-muted">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{customer ? customer.name : <span className="text-muted-foreground">Vybrat (volitelné)…</span>}</span>
                </button>
              </div>
              <div><Label>Platnost do</Label><Input type="date" data-testid="offer-form-valid" className="mt-1.5" value={form.valid_until}
                onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Zrušit</Button>
              <Button onClick={create} data-testid="offer-form-submit">Vytvořit a upravit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="px-10 py-8">
        {offers.length === 0 ? (
          <div className="bg-white border border-border rounded-xl px-6 py-16 text-center">
            <FileText className="h-10 w-10 mx-auto text-muted-foreground/50" />
            <div className="mt-4 font-medium">Zatím žádné nabídky</div>
            <div className="text-sm text-muted-foreground mt-1">Vytvořte nabídku a přidejte položky z kalkulací.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {offers.map((o) => (
              <div key={o.id} data-testid={`offer-card-${o.id}`}
                className="group bg-white border border-border rounded-xl p-6 hover:-translate-y-1 hover:shadow-sm transition-transform duration-200 cursor-pointer"
                onClick={() => nav(`/nabidky/${o.id}`)}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="num text-xs text-muted-foreground">{o.number}</div>
                    <div className="font-display text-lg font-medium leading-tight mt-0.5">{o.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">{o.customer_name || "bez odběratele"}</div>
                  </div>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => duplicate(o.id)} data-testid={`offer-duplicate-${o.id}`} className="text-muted-foreground hover:text-primary p-1"><Copy className="h-4 w-4" /></button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button data-testid={`offer-delete-${o.id}`} className="text-muted-foreground hover:text-destructive p-1"><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Smazat nabídku?</AlertDialogTitle>
                          <AlertDialogDescription>„{o.name}" bude odstraněna.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Zrušit</AlertDialogCancel>
                          <AlertDialogAction onClick={() => del(o.id)}>Smazat</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-border flex items-center justify-between">
                  <Badge variant="outline" className={STATUS[o.status]?.cls}>{STATUS[o.status]?.label || o.status}</Badge>
                  <div className="text-sm text-muted-foreground">{o.items?.length || 0} položek</div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                </div>
                <div className="text-[11px] text-muted-foreground mt-3">
                  {fmtDate(o.created_at)}{o.created_by ? ` · ${o.created_by}` : ""}{o.valid_until ? ` · platnost do ${fmtDate(o.valid_until)}` : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <CustomerPicker open={custOpen} onOpenChange={setCustOpen} onSelect={setCustomer} />
    </div>
  );
}
